import { HorizontalSeparator } from "common/common.styled";
import Button from "components/Button";
import Grid from "components/Grid";
import RadioGroup from "components/RadioGroup";
import RangeSelector from "components/RangeSelector";
import Text from "components/Text";
import { FC, useState } from "react";
import SingleSelect from "components/SingleSelect";
import { IoOptions } from "react-icons/io5";
import { FaSortAmountUp } from "react-icons/fa";
import theme from "theme";
import * as Styled from "./FilterBar.styled";
import { AnimatePresence } from "framer-motion";
import { Props } from "./types";
import { useView } from "hooks";
import { View } from "utils";

export const FilterBar: FC<Props> = ({
    inputs,
    handleInputChange,
    handleAgeRangeSelector,
    handleApply: handleFilterBarApply,
    handleReset: handleFilterBarReset,
    handleSortByChange,
}) => {
    const isMobileView = useView(View.MOBILE_426);
    const [filterMobileMenuActive, setFilterMobileMenuActive] = useState(false);
    const [sortMobileMenuActive, setSortMobileMenuActive] = useState(false);

    const handleApply = () => {
        handleFilterBarApply();
        if (isMobileView) {
            setFilterMobileMenuActive(false);
            setSortMobileMenuActive(false);
        }
    };

    const handleReset = () => {
        handleFilterBarReset();
        if (isMobileView) {
            setFilterMobileMenuActive(false);
            setSortMobileMenuActive(false);
        }
    };

    const handleFilterClick = () => {
        setSortMobileMenuActive(false);
        setFilterMobileMenuActive(!filterMobileMenuActive);
    };

    const handleSortClick = () => {
        setFilterMobileMenuActive(false);
        setSortMobileMenuActive(!sortMobileMenuActive);
    };

    return (
        <Styled.Left>
            <AnimatePresence>
                {(filterMobileMenuActive || !isMobileView) && (
                    <Styled.Menu
                        key="content"
                        initial="collapsed"
                        animate="open"
                        exit="collapsed"
                        variants={{
                            open: { opacity: 1, height: "auto" },
                            collapsed: { opacity: 0, height: 0 },
                        }}
                        transition={{
                            duration: 0.8,
                            ease: [0.04, 0.62, 0.23, 0.98],
                        }}
                    >
                        <Grid
                            row={true}
                            columnGap={1}
                            marginBottom={1}
                            alignItems="center"
                        >
                            <IoOptions
                                color={theme.colors.purple.primary}
                                fontSize="20px"
                            />
                            <Text
                                variant="body3"
                                fontWeight="600"
                                color={theme.colors.grey.secondary}
                            >
                                Filter by
                            </Text>
                        </Grid>
                        <HorizontalSeparator />
                        <Grid
                            column={true}
                            marginTop={1}
                            marginBottom={1}
                            direction="column-direction"
                        >
                            <Text variant="heading6">Gender</Text>
                            <RadioGroup
                                name="gender"
                                label=""
                                value={inputs.gender}
                                columns={2}
                                options={[
                                    { value: "Male", label: "Male" },
                                    { value: "Female", label: "Female" },
                                    { value: "Other", label: "Other" },
                                ]}
                                onChange={handleInputChange}
                            />
                        </Grid>
                        <Grid
                            column={true}
                            marginTop={1}
                            marginBottom={1}
                            rowGap={1}
                            marginRight={1}
                            direction="column-direction"
                        >
                            <Text variant="heading6">Age</Text>
                            <RangeSelector
                                minRange={18}
                                maxRange={70}
                                min={inputs.min_age}
                                max={inputs.max_age}
                                valueUnit="Yrs"
                                size="M"
                                fullWidth
                                onChange={handleAgeRangeSelector}
                            />
                        </Grid>

                        <HorizontalSeparator margin="2rem 0" />
                        <Styled.ApplyResetButtons>
                            <Button
                                fullWidth
                                variant="filled"
                                onClick={handleApply}
                            >
                                Apply
                            </Button>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={handleReset}
                            >
                                Reset
                            </Button>
                        </Styled.ApplyResetButtons>
                    </Styled.Menu>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {sortMobileMenuActive && isMobileView && (
                    <Styled.SortMenu
                        key="content"
                        initial="collapsed"
                        animate="open"
                        exit="collapsed"
                        variants={{
                            open: { opacity: 1, height: "auto" },
                            collapsed: { opacity: 0, height: 0 },
                        }}
                        transition={{
                            duration: 0.8,
                            ease: [0.04, 0.62, 0.23, 0.98],
                        }}
                    >
                        <Styled.SortMenuUpper>
                            <SingleSelect
                                name="sort_by"
                                label="Sort By"
                                minWidth="100%"
                                value={inputs.sort_by}
                                options={[
                                    {
                                        label: "Relevance",
                                        value: "relevance",
                                    },
                                    {
                                        label: "Most Recent",
                                        value: "most_recent",
                                    },
                                ]}
                                placeholder="Relevance"
                                onChange={handleSortByChange}
                            />
                            <HorizontalSeparator margin="2rem 0" />
                        </Styled.SortMenuUpper>

                        <Styled.ApplyResetButtons>
                            <Button
                                fullWidth
                                variant="filled"
                                onClick={handleApply}
                            >
                                Apply
                            </Button>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={handleReset}
                            >
                                Reset
                            </Button>
                        </Styled.ApplyResetButtons>
                    </Styled.SortMenu>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {isMobileView &&
                    !(filterMobileMenuActive || sortMobileMenuActive) && (
                        <Styled.MobileButtonsMenu
                            key="content"
                            initial="collapsed"
                            animate="open"
                            exit="collapsed"
                            variants={{
                                open: { opacity: 1, height: "auto" },
                                collapsed: { opacity: 0, height: 0 },
                            }}
                            transition={{
                                duration: 0.8,
                                ease: [0.04, 0.62, 0.23, 0.98],
                            }}
                        >
                            <Styled.MobileButtons>
                                <Styled.MobileFilterButton
                                    onClick={handleFilterClick}
                                >
                                    <IoOptions
                                        color={theme.colors.purple.primary}
                                        fontSize="20px"
                                    />
                                    Filter
                                </Styled.MobileFilterButton>
                                <Styled.VerticalSeparator />
                                <Styled.MobileFilterButton
                                    onClick={handleSortClick}
                                >
                                    <FaSortAmountUp
                                        color={theme.colors.purple.primary}
                                        fontSize="20px"
                                    />
                                    Sort
                                </Styled.MobileFilterButton>
                            </Styled.MobileButtons>
                        </Styled.MobileButtonsMenu>
                    )}
            </AnimatePresence>
        </Styled.Left>
    );
};
